import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

function isRumEnabled(): boolean {
  const enabledEnv = import.meta.env.VITE_DD_RUM_ENABLED;
  if (enabledEnv?.trim().toLowerCase() === 'false') {
    return false;
  }
  // Treat empty/unset as enabled only when credentials are present, so
  // CI builds with just app id/token configured don't need VITE_DD_RUM_ENABLED=true,
  // while local dev without credentials stays quiet.
  return (
    enabledEnv?.trim().toLowerCase() === 'true' ||
    (!!import.meta.env.VITE_DD_RUM_APPLICATION_ID && !!import.meta.env.VITE_DD_RUM_CLIENT_TOKEN)
  );
}

function getNumberEnvValue(value: string | undefined, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getApiTracingOrigins(): Array<string | RegExp> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) return [];

  try {
    return [new URL(baseUrl, window.location.origin).origin];
  } catch {
    return [];
  }
}

export function initDatadogRum(): void {
  if (!isRumEnabled()) return;

  const applicationId = import.meta.env.VITE_DD_RUM_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DD_RUM_CLIENT_TOKEN;
  const site = import.meta.env.VITE_DD_SITE || 'us5.datadoghq.com';

  if (!applicationId || !clientToken) {
    console.warn(
      'Datadog RUM is enabled but VITE_DD_RUM_APPLICATION_ID or VITE_DD_RUM_CLIENT_TOKEN is missing.',
    );
    return;
  }

  const apiOrigins = getApiTracingOrigins();

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service: import.meta.env.VITE_DD_SERVICE || 'quotehub-frontend',
    env: import.meta.env.VITE_DD_ENV || import.meta.env.MODE,
    version: import.meta.env.VITE_DD_VERSION,
    sessionSampleRate: getNumberEnvValue(import.meta.env.VITE_DD_RUM_SESSION_SAMPLE_RATE, 100),
    sessionReplaySampleRate: getNumberEnvValue(
      import.meta.env.VITE_DD_RUM_SESSION_REPLAY_SAMPLE_RATE,
      20,
    ),
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'allow',
    plugins: [reactPlugin({ router: true })],
    ...(apiOrigins.length > 0
      ? {
          allowedTracingUrls: apiOrigins,
          traceSampleRate: getNumberEnvValue(import.meta.env.VITE_DD_RUM_TRACE_SAMPLE_RATE, 100),
        }
      : {}),
  });
}

export function setDatadogRumUser(user: {
  id: string;
  email?: string;
  name?: string;
}): void {
  if (!isRumEnabled()) return;

  datadogRum.setUser({
    id: user.id,
    email: user.email,
    name: user.name,
  });
}

export function clearDatadogRumUser(): void {
  if (!isRumEnabled()) return;

  datadogRum.clearUser();
}
